import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import isString from 'lodash/isString';
import styled from 'styled-components';
import GroupByDropDown from '../../shared/common/GroupByDropDown';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import { PATH_ORDERS, PATH_RESERVE } from '../../store/constants';
import commonUtils from '../../shared/utils/utils-common';
import VideoPlayerPopup from '../../shared/components/VideoPlayerPopup';
import { ContainerInner } from '../../shared/common/ContainerInner';
import { SidebarContainer } from '../../shared/common/SidebarContainer';
import NoElementsFound, { NO_ELEMENTS_REPORTS } from '../../shared/components/NoElementsFound';
import { THEME_BLACK, THEME_PRIMARY, themeColor } from '../../styles/basics/colors';
import { getReserveGroupByOptions } from './constants';
import { ReserveListSettingsPopup } from './components/ReserveListSettingsPopup';
import { ReserveListDetailPopup } from './components/ReserveListDetailPopup';
import { FinancialYearOut, ReserveListDefaultSettingsOut } from '../../store/api';
import { GroupingState } from '../../store/grouping/state';
import { Button, ButtonSize, ButtonTheme } from '../../shared/common/Button';
import { loadTutorialCookie } from '../../store/authentication/actions';
import { useQueryParams } from '../../shared/hooks/useQueryParams';
import { useHistory, useLocation } from 'react-router';
import { useGetFinancialYears } from './api/financial-years/useGetFinancialYears';
import { useGetReserveReports } from './api/reserve-report/useGetReserveReports';
import { useGenerateReserveReportMutation } from './api/reserve-report/useGenerateReserveReportMutation';
import { useReserveReportSettings } from './api/report-settings/useReserveReportSettings';
import { useNeverShowReserveReportVideoAgainMutation } from './api/useNeverShowReserveReportVideoAgainMutation';
import { useChangeReserveReportSettings } from './api/report-settings/useChangeReserveReportSettings';
import { useTranslation } from 'react-i18next';
import FormatCurrency from '../../shared/utils/formatCurrency';
import classnames from 'classnames';

const HeaderButtonLine = styled.div`
    display: flex;
    justify-content: flex-end;

    > * ~ * {
        margin-left: 5px;
    }
`;

const TrainingButtonLine = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const HeaderFilterLine = styled.div`
    margin-top: 28px;
    display: flex;
    justify-content: flex-end;
    padding: 0;
`;

const StFinancialYearLink = styled.a<{ readonly active?: boolean }>`
    color: ${props => (props.active ? themeColor(THEME_PRIMARY) : themeColor(THEME_BLACK))};
`;

const ReserveReportList: FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();

    const groupByGroups = useMemo(
        () =>
            getReserveGroupByOptions().map(({ key, label }) => ({
                key: key,
                label: t(`reserveReports.groupByDropdown.${label}`, {
                    defaultValue: label
                })
            })),
        [t]
    );

    const [groupBy, setGroupBy] = useState<GroupingState>({ key: '', label: '', id: '' });
    const [selectedFinancialYear, setSelectedFinancialYear] = useState<string | number>('');
    const [showGenerateDialog, setShowGenerateDialog] = useState(false);
    const [showSettingsDialog, setShowSettingsDialog] = useState(false);
    const [showVideo, setShowVideo] = useState(loadTutorialCookie().showReserveVideo);

    const { data: financialYears = [], isFetching: isFinancialYearsLoading } = useGetFinancialYears();
    const { data: reserveReports = [], isFetching: isReservesLoading } = useGetReserveReports(
        selectedFinancialYear,
        groupBy
    );
    const { data: settings, isLoading: isReservesSettingsLoading } = useReserveReportSettings();
    const { mutate: generateReserveReport } = useGenerateReserveReportMutation();
    const doReserveSettingsChange = useChangeReserveReportSettings();
    const { mutate: neverShowReserveVideoAgain } = useNeverShowReserveReportVideoAgainMutation();

    const setFinancialYear = useCallback(
        (financialYear: number) => {
            if (financialYear !== selectedFinancialYear) {
                let tempFinancialYear = financialYear;
                if (isString(financialYear)) {
                    tempFinancialYear = commonUtils.convertStringToNumber(financialYear);
                }
                setSelectedFinancialYear(tempFinancialYear);
            }
        },
        [selectedFinancialYear]
    );

    const changeFinancialYear = useCallback(
        (financialYear: FinancialYearOut) => {
            history.push({
                search: `?financialYear=${financialYear.key}`
            });
            setFinancialYear(financialYear.key!);
        },
        [setFinancialYear, history]
    );

    const {
        financialYear: { value: financialYear }
    } = useQueryParams({
        financialYear: 'number'
    });

    useEffect(() => {
        if (financialYear) {
            setFinancialYear(financialYear);
        } else if (financialYears.length > 0 && !isReservesLoading && !isFinancialYearsLoading) {
            if (history.action === 'POP') {
                history.goBack();
            }
            changeFinancialYear(financialYears[0]);
        }
    }, [
        changeFinancialYear,
        financialYear,
        isFinancialYearsLoading,
        isReservesLoading,
        financialYears,
        setFinancialYear,
        history
    ]);

    const location = useLocation();

    useEffect(() => {
        if (isReservesLoading) {
            const searchQuery = location.search ?? '';
            if (searchQuery.includes('intro')) {
                setShowVideo(true);
            }
        }
    }, [isReservesLoading, location.search]);

    const showSettings = useCallback(() => {
        setShowSettingsDialog(true);
    }, []);

    const closeSettings = useCallback(() => {
        setShowSettingsDialog(false);
    }, []);

    const changeSettings = useCallback(
        (settings: string) => {
            doReserveSettingsChange(settings);
            setShowSettingsDialog(false);
        },
        [doReserveSettingsChange]
    );

    const generate = useCallback(() => {
        setShowGenerateDialog(true);
    }, []);

    const cancelGenerate = useCallback(() => {
        setShowGenerateDialog(false);
    }, []);

    const startGenerate = useCallback(
        (generateSettings: ReserveListDefaultSettingsOut) => {
            setShowGenerateDialog(false);
            generateReserveReport({ generateSettings });
        },
        [generateReserveReport]
    );

    const showIntroVideo = useCallback(() => {
        setShowVideo(true);
    }, []);

    const hideIntroVideo = useCallback(
        (neverShowAgain: boolean = true) => {
            setShowVideo(false);
            if (neverShowAgain) {
                neverShowReserveVideoAgain();
            }
        },
        [neverShowReserveVideoAgain]
    );

    const loading = isReservesLoading || isFinancialYearsLoading || isReservesSettingsLoading;

    return (
        <ContainerInner isLoading={loading}>
            {loading ? (
                <LoadingWrapper>
                    <LoadingOverlay loading={loading} />
                </LoadingWrapper>
            ) : null}
            <VideoPlayerPopup
                showVideo={showVideo}
                hideVideo={hideIntroVideo}
                vimeoId={259902810}
                isTutorialVideoEntryPage={loadTutorialCookie().showReserveVideo}
            />
            <ReserveListDetailPopup
                dispatch={dispatch}
                show={showGenerateDialog}
                onAbort={cancelGenerate}
                onSuccess={startGenerate}
                settings={settings ?? {}}
            />

            <ReserveListSettingsPopup
                show={showSettingsDialog}
                onAbort={closeSettings}
                onSuccessSettingsPopup={changeSettings}
                title={t('reserveReports.defaultSettings.reserveReportSettings')}
                settings={settings ?? {}}
            />

            <SidebarContainer>
                <h2 className="reserve-report-list-heading">{t('reserveReports.reserveReportOverview')}</h2>
                <ul>
                    {financialYears.map(financialYear => (
                        <li key={financialYear.key}>
                            <StFinancialYearLink
                                active={financialYear.key === selectedFinancialYear}
                                onClick={() => changeFinancialYear(financialYear)}
                            >
                                {t('reserveReports.financialYear', {
                                    year: financialYear.value?.split(' ', 2)[1]
                                })}
                            </StFinancialYearLink>
                        </li>
                    ))}
                </ul>
            </SidebarContainer>
            <div style={{ width: '100%' }} data-displayname="ReserveReportsContainer">
                <HeaderButtonLine>
                    <Button
                        text={t('reserveReports.defaultSettings.defaultSettings')}
                        size={ButtonSize.LARGE}
                        theme={ButtonTheme.POSITIVE_INVERT}
                        onClick={showSettings}
                    />
                    <Button
                        text={t('reserveReports.generateNewReport.generateNewReport')}
                        size={ButtonSize.LARGE}
                        onClick={generate}
                    />
                </HeaderButtonLine>
                <TrainingButtonLine>
                    <a onClick={showIntroVideo}>
                        <i className="material-icons fl-left">videocam</i>
                        <span>{t('reserveReports.training')}</span>
                    </a>
                </TrainingButtonLine>
                <HeaderFilterLine className="filter-bar reserve-list-heading-right">
                    {!isReservesLoading ? (
                        <GroupByDropDown
                            groups={groupByGroups}
                            onGroupBy={setGroupBy}
                            current={groupBy}
                            customGroups={true}
                            iconCode={'filter_list'}
                            className="fl-none"
                        />
                    ) : null}
                </HeaderFilterLine>
                <div className={classnames({ 'min-h-screen': isReservesLoading }, 'reserve-list-table')}>
                    {reserveReports.length > 0 ? (
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('reserveReports.reserveReport.date')}</th>
                                    <th>{t('reserveReports.reserveReport.status')}</th>
                                    <th>{t('reserveReports.reserveReport.quantities')}</th>
                                    <th className="rightbound narrow">
                                        {t('reserveReports.reserveReport.totalValue')}
                                    </th>
                                    <th>&nbsp;</th>
                                    <th className="narrow">&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reserveReports.map((entry, i) => {
                                    const status = entry.status?.toLowerCase();
                                    return (
                                        <tr key={i} className={entry.status === 'NEW' ? 'highlight-row' : ''}>
                                            <td>{t('dateFormat.date', { val: new Date(entry.date ?? 0) })}</td>
                                            <td className={entry.status === 'NEW' ? 'highlight' : ''}>
                                                {t(`reserveReports.groupByDropdown.${status}`)}
                                            </td>
                                            <td>
                                                <p>
                                                    {t('reserveReports.eAudiobooks', {
                                                        count: entry.summary?.eaudiobooksAmount ?? 0
                                                    })}
                                                </p>
                                                <p>
                                                    {t('reserveReports.eBooks', {
                                                        count: entry.summary?.ebooksAmount ?? 0
                                                    })}
                                                </p>
                                            </td>
                                            <td className="rightbound">
                                                <span>
                                                    {t('numberFormat.numberWithCurrencySymbol', {
                                                        val: FormatCurrency.convertIntegerToCurrencyFormat(
                                                            entry.summary?.totalNetValue
                                                        ),
                                                        currencySymbol: FormatCurrency.mapCurrencyToSymbol(
                                                            entry.summary?.currency
                                                        )
                                                    })}
                                                </span>
                                            </td>
                                            <td>&nbsp;</td>
                                            <td>
                                                <div style={!entry.orderNumber ? { display: 'table' } : undefined}>
                                                    <Link
                                                        to={`${PATH_RESERVE}/${entry.id}`}
                                                        style={
                                                            !entry.orderNumber ? { display: 'table-cell' } : undefined
                                                        }
                                                        className="black view-report"
                                                    >
                                                        <p>
                                                            <i className="material-icons md-upper mi-green">forward</i>
                                                            {t('reserveReports.reserveReport.viewReport.viewReport')}
                                                        </p>
                                                    </Link>
                                                    {entry.orderNumber ? (
                                                        <Link
                                                            to={`${PATH_ORDERS}/${entry.orderNumber}`}
                                                            className="black view-report"
                                                        >
                                                            <p>
                                                                <i className="material-icons md-upper mi-green">
                                                                    forward
                                                                </i>
                                                                {t('reserveReports.reserveReport.viewOrder')}
                                                            </p>
                                                        </Link>
                                                    ) : (
                                                        <p className="notVisible">&nbsp;</p>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : !isReservesLoading ? (
                        <NoElementsFound type={NO_ELEMENTS_REPORTS} />
                    ) : null}
                </div>
            </div>
        </ContainerInner>
    );
};

export default ReserveReportList;
